import apiService from './apiService';

export const insightsService = {
  async analyzeReflection(data: {
    reflection: string;
    moodScore?: number;
    energyLevel?: number;
    focusLevel?: number;
  }): Promise<any> {
    return apiService.post('/insights/analyze', data);
  },

  async generateSuggestions(data: {
    currentCheckIn: any;
    limit?: number;
  }): Promise<any> {
    return apiService.post('/insights/suggestions', data);
  },

  async getPatterns(days?: number): Promise<any> {
    return apiService.get('/insights/patterns', { params: { days } });
  },

  async getDashboardInsights(): Promise<any> {
    return apiService.get('/insights/dashboard');
  },

  async getPersonalizedInsights(): Promise<any> {
    return apiService.get('/insights/personalized');
  },

  // ALIGNED WITH web_old: Backend fetches teamId from authenticated user
  // GET /insights/team-analysis?type=doing-well|need-attention
  async getTeamAnalysis(type: 'doing-well' | 'need-attention'): Promise<string> {
    const response = await apiService.get<{ analysis: string }>('/insights/team-analysis', { params: { type } });
    return response?.analysis || (typeof response === 'string' ? response : '');
  },

  // ALIGNED WITH web_old: Get growth suggestions with forceRefresh support
  // Backend endpoint: GET /insights/growth-suggestions?forceRefresh=false
  // MIGRATED FROM web_old/src/components/growth/GrowthAISuggestionsWidget.tsx::generateGrowthSuggestions()
  async getGrowthSuggestions(forceRefresh: boolean = false): Promise<any> {
    return apiService.get('/insights/growth-suggestions', { params: { forceRefresh } });
  },

  // ALIGNED WITH web_old: Generate AI growth insights with forceRefresh support
  // Backend endpoint: GET /insights/ai-growth?forceRefresh=false
  // MIGRATED FROM web_old/src/components/growth/AIGrowthInsights.tsx::generateGrowthInsights()
  async getAIGrowthInsights(forceRefresh: boolean = false): Promise<any> {
    return apiService.get('/insights/ai-growth', { params: { forceRefresh } });
  },

  async getUnifiedInsights(teamId?: string | null): Promise<any> {
    return apiService.get('/insights/unified', { params: teamId ? { teamId } : {} });
  },

  async getEnhancedPatterns(days: number = 30): Promise<any> {
    return apiService.get('/insights/enhanced-patterns', { params: { days } });
  },

  // ALIGNED WITH web_old: Generate contextual AI suggestions based on enhanced patterns
  // Backend endpoint: GET /insights/enhanced-patterns/suggestions?days=30
  // MIGRATED FROM web_old/src/components/dashboard/EnhancedInsightsPanel.tsx::generateContextualSuggestions()
  async getContextualSuggestions(days: number = 30): Promise<string[]> {
    const response = await apiService.get<string[]>('/insights/enhanced-patterns/suggestions', { params: { days } });
    return Array.isArray(response) ? response : [];
  },

  // ALIGNED WITH web_old: Generate team AI insights (for TeamAISuggestionsWidget)
  // Backend endpoint: GET /insights/team-insights?forceRefresh=false
  // MIGRATED FROM web_old/src/components/dashboard/TeamAISuggestionsWidget.tsx::generateTeamInsights()
  async getTeamInsights(forceRefresh: boolean = false): Promise<any[]> {
    const response = await apiService.get<any[]>('/insights/team-insights', { params: { forceRefresh } });
    return Array.isArray(response) ? response : [];
  },

  async getPatternAnalysis(days: number = 30): Promise<any> {
    return apiService.get('/insights/pattern-analysis', { params: { days } });
  },

  async getActionableInsights(): Promise<any> {
    return apiService.get('/insights/actionable-insights');
  },

  // MIGRATED TO BACKEND: Get user pattern analysis (UserPatternAnalysis format matching web_old)
  // Backend endpoint: GET /insights/user-pattern-analysis
  // MIGRATED FROM web_old/src/services/patternPredictionService.ts
  async getUserPatternAnalysis(days: number = 30): Promise<any> {
    return apiService.get('/insights/user-pattern-analysis', { params: { days } });
  },

  // MIGRATED TO BACKEND: Generate insights for Weekly Insights Panel (string[] format)
  // Backend endpoint: GET /insights/generate-insights
  // MIGRATED FROM web_old/src/services/openaiService.ts::generateInsights()
  async getGenerateInsights(): Promise<string[]> {
    return apiService.get('/insights/generate-insights');
  },
};
